// The review of an imported patient, shown by PatientImportReview.open() - straight away when its record is
// opened, or when it is chosen for a task. Duplicates come first: there is no point completing the details of a
// patient who turns out to be someone already in CatTrax. Then either:
//  - merge: the existing patient keeps its record and takes the details chosen from the import, along with the
//    imported patient's appointments and documents; the imported patient is deleted; or
//  - confirm: a new patient, once its registration details are complete. The full details form is here (the
//    same patientDetailsForm the dashboard uses), so an NZ address can be picked from the lookup in the review.
// Closes with { patient }: the patient to carry on with.
//
// PatientImportReview loads the review and the patient before showing this, so the dialog opens on its first step
// at that step's width; review is null when they couldn't be loaded.
app.controller("patientImportReviewController", [
	"$scope",
	"$q",
	"$mdDialog",
	"Session",
	"countryConfig",
	"PatientAPI",
	"PatientImportAPI",
	"PatientRequiredDetails",
	"patientId",
	"options",
	"review",
	"patient",
	function ($scope, $q, $mdDialog, Session, countryConfig, PatientAPI, PatientImportAPI, PatientRequiredDetails, patientId, options, review, patient) {
		// The details that can be taken from the imported patient in a merge, in the order they are shown.
		const MERGE_FIELDS = [
			{ key: "PREFERRED_NAME", label: "Preferred name", value: (p) => p.preferredName },
			{ key: "MOBILE_NUMBER", label: "Mobile", value: (p) => p.mobileNumber },
			{ key: "PHONE_NUMBER", label: "Phone", value: (p) => p.phoneNumber },
			{ key: "EMAIL", label: "Email", value: (p) => p.email },
			{ key: "NHI", label: "NHI", value: (p) => p.nhi, onlyWhenTargetBlank: true },
			{ key: "GENDER", label: "Gender", value: (p) => titleCase(p.gender) },
			{ key: "ADDRESS", label: "Address", value: (p) => formatAddresses(p.addresses) },
			{ key: "ETHNICITY", label: "Ethnicity", value: (p) => formatEthnicities(p) },
			{ key: "RESIDENT", label: "Resident", value: (p) => yesNo(p.isResident) },
			{ key: "INTERPRETER", label: "Interpreter", value: (p) => formatInterpreter(p) },
			{ key: "NOTE", label: "Note", value: (p) => p.note },
		];

		$scope.options = options;
		$scope.busy = false;
		$scope.error = null;
		// The details <form> publishes itself here: it sits inside an ng-if, whose scope the actions can't see.
		$scope.forms = {};
		$scope.missing = [];

		// The details form's API (see patientDetailsForm), once it is up.
		let detailsForm = null;
		$scope.registerDetailsForm = function (form) {
			detailsForm = form;
		};

		// What is still needed follows the edits as they are made, so the confirm waits until they are complete. The
		// required details are PatientRequiredDetails', which the server's confirm checks too.
		$scope.$watch(
			() => ($scope.draft ? PatientRequiredDetails.missing($scope.draft, detailsForm) : []),
			(missing) => {
				$scope.missing = missing;
			},
			true
		);

		// Every required detail is filled in: the server's list, and the form's own required fields (name, date of
		// birth) and formats.
		$scope.detailsComplete = function () {
			const form = $scope.forms.reviewDetails;
			return !$scope.missing.length && !!form && form.$valid;
		};

		$scope.close = function () {
			$mdDialog.cancel();
		};

		function start() {
			if (!review || !patient) {
				$scope.step = "error";
				$scope.error = "The imported patient couldn't be loaded. Close this and try again.";
				return;
			}

			$scope.review = review;
			setPatient(patient);

			const firstImport = (review.imports || [])[0];
			$scope.importedFrom = firstImport
				? {
						label: ["Imported", firstImport.source_system ? "from " + titleCase(firstImport.source_system) : null]
							.filter(Boolean).join(" ")
							+ (firstImport.imported_date ? " · " + moment(firstImport.imported_date).locale("en").format("D MMM YYYY") : ""),
				  }
				: null;

			$scope.candidates = (review.merge_candidates || []).map(function (candidate) {
				return {
					summary: candidate,
					name: [candidate.firstName, candidate.lastName].filter(Boolean).join(" "),
					dob: formatDob(candidate.dateOfBirth),
					practiceName: candidate.practice && candidate.practice.name,
					canMerge: canAt(candidate.practice, "PATIENT_IMPORT_MERGE"),
				};
			});
			$scope.canUpdate = canAt($scope.patient.practice, "PATIENT_UPDATE");
			$scope.canConfirm = canAt($scope.patient.practice, "PATIENT_IMPORT_REVIEW");

			if (review.status === "MERGING") {
				$scope.step = "resume";
			} else {
				$scope.step = $scope.candidates.length ? "duplicates" : "details";
			}
		}

		// The patient as saved, and the copy the details form edits, so closing the review throws edits away.
		function setPatient(saved) {
			$scope.patient = saved;
			$scope.draft = angular.copy(saved);
		}

		// Permissions are per practice, and the user only knows those of practices they work at.
		function canAt(practice, permission) {
			if (!practice) {
				return false;
			}
			const known = (Session.practiceOptions || []).find((p) => p.id === practice.id);
			if (known) {
				return Session.hasPermissionAt(known, permission);
			}
			return !!Session.practice && Session.practice.id === practice.id && Session.hasPermission(permission);
		}

		// ---- Duplicates and merging ----------------------------------------------------------------------

		$scope.chooseCandidate = function (candidate) {
			if (!candidate.canMerge || $scope.busy) {
				return;
			}
			$scope.busy = true;
			PatientAPI.getPatient(candidate.summary.id)
				.then(function (resp) {
					$scope.target = resp.data;
					$scope.targetName = candidate.name;
					$scope.targetPractice = candidate.practiceName;
					$scope.rows = MERGE_FIELDS.map(function (field) {
						const existing = field.value($scope.target) || "";
						const imported = field.value($scope.patient) || "";
						const available = !!imported && imported !== existing && !(field.onlyWhenTargetBlank && existing);
						return {
							key: field.key,
							label: field.label,
							existing: existing,
							imported: imported,
							// Nothing to choose between: the import holds what the record already does.
							same: !!imported && imported === existing,
							available: available,
							// Fill the gaps by default; replacing what the existing record holds is a choice.
							take: available && !existing,
						};
					}).filter((row) => row.existing || row.imported);
					// Only what the import holds is a choice; the rest of the record is simply kept.
					$scope.keptCount = $scope.rows.filter((row) => !row.imported).length;
					$scope.rows = $scope.rows.filter((row) => row.imported);
					$scope.step = "compare";
				})
				.catch(function () {
					$scope.error = "That patient couldn't be loaded. Try again.";
				})
				.finally(function () {
					$scope.busy = false;
				});
		};

		$scope.backToDuplicates = function () {
			$scope.error = null;
			$scope.step = "duplicates";
		};

		// Leaving the registration without saving: back to the duplicates if there were any, else out of the review.
		$scope.returnFromDetails = function () {
			if ($scope.candidates && $scope.candidates.length) {
				$scope.backToDuplicates();
			} else {
				$scope.close();
			}
		};

		$scope.notADuplicate = function () {
			$scope.error = null;
			$scope.step = "details";
		};

		$scope.merge = function () {
			const fields = ($scope.rows || []).filter((row) => row.available && row.take).map((row) => row.key);
			$scope.busy = true;
			$scope.error = null;
			PatientImportAPI.merge(patientId, $scope.target.id, fields)
				.then(function (resp) {
					$mdDialog.hide({ patient: resp.data, merged: true });
				})
				.catch(function (resp) {
					const message = (resp && resp.data && resp.data.message) || "The merge didn't finish. Try again.";
					// A merge that stopped part-way is left for CatTrax support to finish, so the review says so rather
					// than offering to merge again. One refused before it started can simply be tried again.
					return PatientImportAPI.getReview(patientId).then(
						function (reviewResp) {
							if (reviewResp.data && reviewResp.data.status === "MERGING") {
								$scope.step = "resume";
							} else {
								$scope.error = message;
							}
						},
						function () {
							$scope.error = message;
						}
					);
				})
				.finally(function () {
					$scope.busy = false;
				});
		};

		// ---- Completing the details and confirming ----------------------------------------------------------

		// Saves the details form. Resolves with the saved patient, or rejects (with the reason shown) when it
		// can't be saved yet.
		function saveDetails() {
			if (!detailsForm) {
				return $q.reject();
			}
			$scope.error = null;

			// Writes the address picked from the lookup and the date of birth onto the copy; false (with the address
			// field selected and flagged) when an NZ address was typed rather than picked.
			if (!detailsForm.prepareForUpdate()) {
				$scope.error = countryConfig.countryCode === "NZ"
					? "Choose the patient's address from the address lookup, so their DHB can be found."
					: "Check the patient's address.";
				return $q.reject();
			}

			return PatientAPI.update($scope.draft).then(
				function (resp) {
					setPatient(resp.data);
					return resp.data;
				},
				function (resp) {
					$scope.error = saveErrorMessage(resp);
					return $q.reject(resp);
				}
			);
		}

		function saveErrorMessage(resp) {
			const data = resp && resp.data;
			if (data && Array.isArray(data.error)) {
				return "Some details were rejected: " + data.error.join("; ");
			}
			return (data && (data.message || (typeof data.error === "string" && data.error))) || "The details couldn't be saved. Check them and try again.";
		}

		$scope.saveAndConfirm = function () {
			if ($scope.busy || !$scope.canUpdate || !$scope.canConfirm || !$scope.detailsComplete()) {
				return;
			}
			$scope.busy = true;
			saveDetails()
				.then(function () {
					return PatientImportAPI.confirm(patientId).then(
						function (resp) {
							$mdDialog.hide({ patient: resp.data, confirmed: true });
						},
						function (resp) {
							// Their details were saved, and what is still missing is shown from them.
							$scope.error = (resp && resp.data && resp.data.message) || "The patient couldn't be confirmed. Try again.";
						}
					);
				})
				.catch(angular.noop)
				.finally(function () {
					$scope.busy = false;
				});
		};

		// ---- Formatting ---------------------------------------------------------------------------------

		function titleCase(value) {
			if (!value) {
				return "";
			}
			return String(value)
				.toLowerCase()
				.split("_")
				.map((word) => (word === "nz" ? "NZ" : word.charAt(0).toUpperCase() + word.slice(1)))
				.join(" ");
		}

		function yesNo(value) {
			return value === true ? "Yes" : value === false ? "No" : "";
		}

		function formatDob(value) {
			return value && moment(value).isValid() ? moment(value).locale("en").format("D MMM YYYY") : "";
		}

		function formatAddresses(addresses) {
			const address = (addresses || []).find((a) => a.type === "PHYSICAL") || (addresses || [])[0];
			if (!address) {
				return "";
			}
			return [address.apartmentNumber, address.line1, address.line2, address.city, address.postCode]
				.filter(Boolean)
				.join(", ");
		}

		function formatEthnicities(patient) {
			const names = (patient.ethnicities || []).filter((e) => e !== "OTHER").map(titleCase);
			if (patient.otherEthnicities) {
				names.push(patient.otherEthnicities);
			}
			return names.join(", ");
		}

		function formatInterpreter(patient) {
			if (patient.isInterpreterNeeded === true) {
				return "Yes" + (patient.interpreterLanguage ? " - " + patient.interpreterLanguage : "");
			}
			return yesNo(patient.isInterpreterNeeded);
		}

		$scope.patientName = function () {
			return $scope.patient ? [$scope.patient.firstName, $scope.patient.lastName].filter(Boolean).join(" ") : "";
		};

		$scope.patientDob = function () {
			return $scope.patient ? formatDob($scope.patient.dateOfBirth) : "";
		};

		$scope.initials = function () {
			if (!$scope.patient) {
				return "";
			}
			return [$scope.patient.firstName, $scope.patient.lastName]
				.filter(Boolean)
				.map((name) => name.trim().charAt(0).toUpperCase())
				.join("");
		};

		start();
	},
]);
